/**
 * Platform Admin > Features > "Forecast attribution per customer".
 *
 * Sits directly beneath the delivery-grain panel because the two answer
 * consecutive questions about the same customer:
 *
 *   PO Line Schedules            platform default
 *   └─ Delivery grain            does this customer use schedule lines?
 *      └─ Forecast attribution   if not, does its feed name the supplier?
 *
 * Most feeds name a supplier on every forecast line, and that line is that
 * supplier's demand. Some do not: the customer plans demand per PART, so any
 * approved supplier of that part could be asked to meet it, and two suppliers
 * of one part both see it. That is a planning model, not a broken feed, which
 * is why it is declared here rather than guessed from the data.
 *
 * A schedule-based customer derives forecast from schedule lines and never
 * consults this at all — those rows show as read-only so the setting can never
 * appear to contradict the grain above it.
 *
 * Rows save immediately (one PATCH per change), independent of the platform
 * settings Save button. Helix-only.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  platformSettingsAPI,
  type CustomerForecastScope,
  type ForecastScope,
} from '../../../lib/api';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../ui/select';
import { GitBranch, Loader2, RefreshCw, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const SCOPE_LABELS: Record<ForecastScope, string> = {
  supplier: 'Supplier named in feed',
  part: 'Part-level demand',
};

const MODE_LABELS: Record<'schedule' | ForecastScope, string> = {
  schedule: 'Schedule lines',
  supplier: 'Supplier named in feed',
  part: 'Part-level demand',
};

export function ForecastScopePanel() {
  const [customers, setCustomers] = useState<CustomerForecastScope[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await platformSettingsAPI.getForecastScopes();
      setCustomers(res.data.customers ?? []);
    } catch (err: unknown) {
      console.error('Failed to load forecast scope settings:', err);
      setError('Could not load the per-customer forecast attribution. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onChange = async (companyId: number, scope: ForecastScope) => {
    setSavingId(companyId);
    try {
      const res = await platformSettingsAPI.updateForecastScope(companyId, scope);
      setCustomers(prev => prev.map(c => (
        c.id === companyId
          ? { ...c, forecast_scope: res.data.forecast_scope, effective_mode: res.data.effective_mode }
          : c
      )));
      toast.success(`Forecast attribution for ${res.data.name}: ${SCOPE_LABELS[res.data.forecast_scope]}`);
    } catch (err: unknown) {
      console.error('Failed to update forecast scope:', err);
      toast.error('Failed to update forecast attribution');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="p-4 border rounded-lg" data-testid="forecast-scope-panel">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-start gap-3">
          <GitBranch className="h-5 w-5 text-primary mt-0.5" />
          <div>
            <Label className="text-base font-medium">Forecast attribution per customer</Label>
            <p className="text-sm text-muted-foreground">
              How this customer&apos;s forecast lines are attributed to suppliers.{' '}
              <span className="font-medium">Supplier named in feed</span> — each line names its
              supplier and only that supplier sees it.{' '}
              <span className="font-medium">Part-level demand</span> — the feed carries no supplier,
              so every supplier of that part sees the demand and the customer still counts it once.
              Customers on schedule lines derive forecast from their schedules and ignore this.
            </p>
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={load} disabled={loading} aria-label="Reload forecast attribution">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </Button>
      </div>

      {loading && customers.length === 0 && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading customers…
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 text-sm text-destructive py-2" role="alert">
          <AlertCircle className="h-4 w-4" /> {error}
        </div>
      )}

      {!loading && !error && customers.length === 0 && (
        <p className="text-sm text-muted-foreground py-2">No customer companies on this install yet.</p>
      )}

      {customers.length > 0 && (
        <div className="space-y-2">
          {customers.map((c) => (
            <div
              key={c.id}
              data-testid={`forecast-scope-row-${c.id}`}
              className="flex flex-wrap items-center justify-between gap-2 border rounded-md px-3 py-2"
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className="font-medium truncate">{c.name}</span>
                {!c.is_active && <Badge variant="outline">Inactive</Badge>}
                <Badge variant={c.schedule_based ? 'secondary' : 'outline'}>
                  Effective: {MODE_LABELS[c.effective_mode]}
                </Badge>
              </div>
              <div className="flex items-center gap-2">
                {savingId === c.id && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                {c.schedule_based ? (
                  // Not a disabled dropdown pretending to be settable: this
                  // customer's forecast comes from schedule lines, so there is
                  // no attribution choice to make. Say so.
                  <span className="text-sm text-muted-foreground italic pr-1">
                    Set by delivery grain
                  </span>
                ) : (
                  <Select
                    value={c.forecast_scope}
                    onValueChange={(v) => onChange(c.id, v as ForecastScope)}
                    disabled={savingId === c.id}
                  >
                    <SelectTrigger className="w-[220px]" aria-label={`Forecast attribution for ${c.name}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(Object.keys(SCOPE_LABELS) as ForecastScope[]).map((v) => (
                        <SelectItem key={v} value={v}>{SCOPE_LABELS[v]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
